/**
 * Support for all agenda related repository.
 * Column and agenda repository...
 */

import { defineStore } from "pinia";
import type {FormEnvironment} from "@fd/Types.ts";
import type {AgendaDescriptor, AgendaRegistryEntry} from "@t/agenda/Agenda.ts";
import {ViewProps} from "@t/agenda/View.ts";
import {ColumnListItem} from "@t/agenda/Columns.ts";
import {env} from "@fd/Environment.ts";

export function buildAgendaKey(agenda: string, entity?: string, viewProps?: ViewProps):string {
	let key:string = agenda;
	if (entity) key = key + "|" + entity;
	if (typeof viewProps === 'object') {
		const propArray:string[] = [];
		for (let vp in viewProps) {
			if (viewProps.hasOwnProperty(vp)) {
				propArray.push(vp + "=" + encodeURIComponent(viewProps[vp]));
			}
		}
		if (propArray.length > 0) {
			propArray.sort();	// must be sorted to ensure uniqueness
			key = key + '|' + propArray.join("|");
		}
	}
	return key;
}

/**
 * Test if the segment is viewprop. If yes, it is added to vp object, if no false is returned
 */
function addedViewProp(segment:string, vp:ViewProps):boolean {
	if (segment == null || segment.length === 0) return false;
	const eqIdx = segment.indexOf('=');
	if (eqIdx > 0) {
		const propName = segment.substring(0, eqIdx);
		vp[propName] = decodeURIComponent(segment.substring(eqIdx + 1));
		return true;
	}
	return false;
}


export function parseAgendaKey(key:string):AgendaDescriptor|null {
	if (key == null || key.length === 0) return null;
	const bar = key.split("|");
	const desc:AgendaDescriptor = { agenda: bar[0] };
	const props:ViewProps = {};

	// Parse remaining segments
	for (let i = 1; i < bar.length; i++) {
		const segment = bar[i];
		if (!addedViewProp(segment, props)) {
			if (segment && !desc.entity) desc.entity = segment;
		}
	}
	if (Object.keys(props).length > 0) desc.props = props;
	return desc;
}


async function loadAgendaRegistry(env:FormEnvironment):Promise<AgendaRegistryEntry[]> {
	return env.net.fetch("api/agenda", "GET")
		.then((response) => {
			if (response.ok) return response.json() as Promise<AgendaRegistryEntry[]>;
			else throw new Error("Cannot load agenda descriptors");
		});
}

async function loadAgendaColumns(env:FormEnvironment, agenda:string, entity?:string|null, viewProps?:ViewProps|null):Promise<ColumnListItem[]> {
	let url = "api/agenda/columns";
	url = env.net.urlParam(url, "agenda", agenda);
	if (entity) url = env.net.urlParam(url, "entity", entity);
	if (viewProps) {
		for (let vp in viewProps) {
			if (viewProps.hasOwnProperty(vp)) {
				url = env.net.urlParam(url, vp, viewProps[vp]);
			}
		}
	}
	return env.net.fetch(url, "GET")
		.then((response) => {
			if (response.ok) return response.json() as Promise<ColumnListItem[]>;
			else new Error("Unable to load columns from " + url);
		});
}

function extractViewProps(agendaObject:AgendaRegistryEntry):ViewProps|null {
	if (agendaObject) {
		if (agendaObject.view) {
			if (agendaObject.view.props != null) return agendaObject.view.props;
		}
	}
	return null;
}

function objectMatch(vp1?:ViewProps|null, vp2?:ViewProps|null) {

	if (vp1 == null) vp1 = {};
	if (vp2 == null) vp2 = {};
	const props1 = Object.keys(vp1);
	const props2 = Object.keys(vp2);

	if (props1.length !== props2.length) return false;
	for (let i = 0; i < props1.length; i++) {
		if (vp1[props1[i]] !== vp2[props1[i]]) return false;
	}
	return true;
}


export const useAgendaStore = defineStore("agenda", () => {

	let registry:Promise<AgendaRegistryEntry[]> = loadAgendaRegistry(env);

	/** Return agenda metadata or null if not found */
	async function lookupRegistry(desc:AgendaDescriptor):Promise<AgendaRegistryEntry|null> {
		const entries = await registry;

		for (let are of entries) {
			if (desc.agenda === are.agenda) {
				if (desc.entity && desc.entity !== are.entity) continue;
				const dprops = extractViewProps(are);
				if (objectMatch(dprops, desc.props)) return are;
			}
		}
		return null;
	}

	const columns = new Map<string, Promise<ColumnListItem[]>>();

	async function getColumns(desc:AgendaDescriptor):Promise<ColumnListItem[]> {
		const key = buildAgendaKey(desc.agenda, desc.entity, desc.props);
		let cols = columns.get(key);
		if (!cols) {
			cols = loadAgendaColumns(env, desc.agenda, desc.entity, desc.props);
			columns.set(key, cols);
		}
		return cols;
	}

	async function getFilterColumns(desc:AgendaDescriptor):Promise<ColumnListItem[]> {
		const all = await getColumns(desc);
		return all.filter((col:ColumnListItem) => !col.noFilter);
	}

	async function getVisibleColumns(desc:AgendaDescriptor):Promise<ColumnListItem[]> {
		const all = await getColumns(desc);
		return all.filter((col:ColumnListItem) => !col.noView);
	}

	return {
		lookupRegistry,
		getColumns,
		getFilterColumns,
		getVisibleColumns,
	};
});